import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';

import ContentCard from './components/ContentCard';
import NewContentCarousel from './components/NewContentCarousel';
import CategoryFilter from './components/CategoryFilter';
import HallOfFameSection from './components/HallOfFameSection';

import { categoryService } from '../../services/categoryService';
import { plansService } from '../../services/plansService';
import { engagementService } from '../../services/engagementService';

import useCartStore from '../../store/cartStore';
import useContentStore from '../../store/contentStore';
import Button from '../../components/ui/Button';
import { Link } from 'react-router-dom';



const HomeGallery = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { getItemCount, initSession } = useCartStore();
  
  // Use centralized content store
  const { 
    contentItems, 
    fetchContent, 
    subscribeToChanges, 
    unsubscribeFromChanges 
  } = useContentStore();
  
  const logoUrl = ''; // or define the actual logo URL path

  useEffect(() => {
    initSession();
    loadData();
    
    // Subscribe to real-time updates
    subscribeToChanges();
    
    // Cleanup subscription on unmount
    return () => {
      unsubscribeFromChanges();
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoryData, plansData] = await Promise.all([
        categoryService?.getAll(),
        plansService?.getAll({ isActive: true }),
        fetchContent() // Fetch from centralized store
      ]);
      setCategories(categoryData);
      setPlans(plansData);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleContentClick = async (contentId) => {
    try {
      await engagementService?.trackView(contentId);
    } catch (err) {
      console.error('Error tracking view:', err);
    }
  };

  const filteredContent = selectedCategory === 'all'
    ? contentItems
    : contentItems?.filter(item => item?.categoryId === selectedCategory);

  const featuredContent = contentItems?.filter(item => item?.isFeatured === true)?.slice(0, 5);

  const topRatedContent = useMemo(() => {
    return [...contentItems]?.sort((a, b) => b?.rating - a?.rating)?.slice(0, 6);
  }, [contentItems]);

  const newContent = useMemo(() => {
    return [...contentItems]?.sort((a, b) => new Date(b?.uploadDate) - new Date(a?.uploadDate))?.slice(0, 9);
  }, [contentItems]);

  const handleWhatsAppRequest = (content) => {
    const message = `Olá UP MIDIA! 👋\n\nGostaria de solicitar informações sobre:\n\n📌 ${content?.title}\n🎨 Categoria: ${content?.category}\n⭐ Avaliação: ${content?.rating} estrelas\n\nAguardo retorno!\n\n*Enviado via UP MIDIA Gallery*`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/5511999999999?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <img
                  src="/assets/images/up_menor-1769135382338.png"
                  alt="UP MIDIA platform logo"
                  className="relative h-10 md:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-primary">
                  UP MIDIA
                </h1>
                <p className="text-xs text-muted-foreground hidden md:block">
                  Artes e Vídeos Profissionais
                </p>
              </div>
            </Link>
            
            <nav className="hidden md:flex items-center gap-4">
              <Link to="/" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                Início
              </Link>
              <Link to="/category-gallery" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                Galeria
              </Link>
              <Link to="/hall-of-fame" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                Hall da Fama
              </Link>
              <Link to="/admin-login" className="text-sm font-medium text-accent hover:text-accent/80 transition-colors">
                Admin Login
              </Link>
              <Link to="/shopping-cart">
                <Button variant="outline" iconName="ShoppingCart" className="relative">
                  Carrinho
                  {getItemCount() > 0 && (
                    <span className="absolute -top-2 -right-2 w-6 h-6 bg-accent text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {getItemCount()}
                    </span>
                  )}
                </Button>
              </Link>
            </nav>

            <div className="flex md:hidden items-center gap-2">
              <Link to="/shopping-cart">
                <Button variant="outline" size="sm" iconName="ShoppingCart" className="relative">
                  {getItemCount() > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {getItemCount()}
                    </span>
                  )}
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                iconName={mobileMenuOpen ? 'X' : 'Menu'}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              />
            </div>
          </div>

          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pt-4 border-t border-border flex flex-col gap-3">
              <Link 
                to="/" 
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Início
              </Link>
              <Link 
                to="/category-gallery" 
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Galeria
              </Link>
              <Link 
                to="/hall-of-fame" 
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Hall da Fama
              </Link>
              <Link 
                to="/admin-login" 
                className="text-sm font-medium text-accent hover:text-accent/80 transition-smooth py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin Login
              </Link>
            </nav>
          )}
        </div>
      </header>
      {error && (
        <div className="mx-4 mt-4 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
          {error}
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando galeria...</p>
          </div>
        </div>
      ) : (
        <>
          <main className="pt-20 px-4 md:px-6 lg:px-10 pb-12 md:pb-16 lg:pb-20">
            <div className="max-w-7xl mx-auto space-y-8 md:space-y-12 lg:space-y-16">
              {logoUrl && (
                <section className="container mx-auto px-4 py-8">
                  <div className="flex justify-center">
                    <div className="relative group">
                      <div className="relative bg-card rounded-lg p-6 shadow-lg">
                        <Image
                          src="/assets/images/up_menor-1769132654249.png"
                          alt="UP MIDIA - Logo oficial da empresa de produção de conteúdo audiovisual e design gráfico"
                          className="h-24 w-auto object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </section>
              )}

              <section>
                <NewContentCarousel 
                  newContent={newContent}
                  onWhatsAppRequest={handleWhatsAppRequest}
                />
              </section>

              <section>
                <HallOfFameSection 
                  topContent={topRatedContent}
                  onWhatsAppRequest={handleWhatsAppRequest}
                />
              </section>

              <section>
                <CategoryFilter
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  contentCount={filteredContent?.length}
                />
              </section>

              {plans?.length > 0 && (
                <section>
                  <div className="mb-6 md:mb-8">
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                      Planos de Assinatura
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Escolha o plano ideal para suas necessidades
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {plans?.map((plan) => (
                      <div
                        key={plan?.id}
                        className="bg-card rounded-lg overflow-hidden glow-primary hover:glow-primary-hover transition-smooth"
                      >
                        <div className="relative h-48">
                          <Image
                            src={plan?.coverImage}
                            alt={`Capa do plano ${plan?.name}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-6">
                          <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                            {plan?.name}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                            {plan?.description}
                          </p>
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-2xl font-bold text-accent">
                              R$ {plan?.price?.toFixed(2)}
                            </span>
                          </div>
                          <Button
                            onClick={() => {
                              useCartStore?.getState()?.addItem({
                                id: plan?.id,
                                type: 'plan',
                                name: plan?.name,
                                coverImage: plan?.coverImage,
                                description: plan?.description,
                                price: plan?.price
                              });
                            }}
                            iconName="ShoppingCart"
                            fullWidth
                            className="glow-primary"
                          >
                            Adicionar ao Carrinho
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <div className="mb-6 md:mb-8">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                    Galeria de Conteúdo
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Explore nossa coleção de artes e vídeos
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                  {filteredContent?.map((content) => (
                    <ContentCard
                      key={content?.id}
                      content={content}
                      onContentClick={handleContentClick}
                    />
                  ))}
                </div>

                {filteredContent?.length === 0 && (
                  <div className="text-center py-12 md:py-16">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 glow-primary">
                      <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2">
                      Nenhum conteúdo encontrado
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Tente selecionar outra categoria
                    </p>
                  </div>
                )}
              </section>
            </div>
          </main>
        </>
      )}
    </div>
  );
};

export default HomeGallery;